import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, User, LogOut, Shield, ShoppingCart } from "lucide-react";
import { api } from "../../lib/api";
import { Logo } from "./Logo";
import { useLang } from "../../lib/i18n";
import { useAuth } from "../../lib/auth";
import { ThemeToggle, useTheme } from "../../lib/theme";
import { InstallAppButton } from "./InstallAppButton";

export const LangSwitch = ({ light }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex rounded-full p-1 text-xs font-bold ${light ? "bg-white/10" : "bg-slate-100"}`} data-testid="language-switcher">
      {["en", "it", "es"].map((l) => (
        <button key={l} data-testid={`language-select-${l}`} onClick={() => setLang(l)}
          className={`rounded-full px-2.5 py-1 uppercase transition-colors ${lang === l ? "bg-[#FF5722] text-white" : light ? "text-white hover:text-white" : "text-slate-500 hover:text-slate-900"}`}>
          {l}
        </button>
      ))}
    </div>
  );
};

const CartLink = ({ overHero }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    const load = () => api.get("/me/cart").then((r) => setN(r.data.items.length)).catch(() => {});
    load();
    window.addEventListener("aa-cart", load);
    return () => window.removeEventListener("aa-cart", load);
  }, []);
  return (
    <Link to="/cart" className={`relative ${overHero ? "text-white" : "text-[#0A2540]"}`} title="Cart" data-testid="nav-cart">
      <ShoppingCart size={20} />
      {n > 0 && <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#FF5722] px-1 text-[10px] font-bold text-white" data-testid="nav-cart-count">{n}</span>}
    </Link>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const { user, signOut } = useAuth();
  const loc = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { dark } = useTheme();
  const overHero = ["/", "/about"].includes(loc.pathname) && !scrolled;

  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 40);
    f();
    window.addEventListener("scroll", f);
    return () => window.removeEventListener("scroll", f);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const links = [["/", t("nav_home")], ["/about", t("nav_about")], ["/clinics", t("nav_clinics")], ["/partners", t("nav_partners")], ["/contacts", t("nav_contacts")]];
  const linkCls = ({ isActive }) => `whitespace-nowrap text-sm font-semibold transition-colors ${overHero ? (isActive ? "text-white" : "text-white/75 hover:text-white") : isActive ? "text-[#0A2540]" : "text-slate-500 hover:text-[#0A2540]"}`;

  return (
    <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${overHero ? "bg-transparent" : "border-b border-slate-200/70 bg-white/80 backdrop-blur-xl"}`}>
      <div className="container-x flex h-20 items-center justify-between gap-4 xl:gap-6">
        <Logo light={overHero || dark} />
        <nav className="hidden items-center gap-5 lg:flex xl:gap-8">
          {links.map(([to, label]) => (
            <NavLink key={to} to={to} end={to === "/"} className={linkCls} data-testid={`nav-link-${to.replace("/", "") || "home"}`}>{label}</NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <LangSwitch light={overHero} />
          <ThemeToggle light={overHero} />
          <InstallAppButton overHero={overHero} />
          {user ? (
            <>
              {user.role === "collaborator" && <Link to="/admin/proforma" data-testid="nav-collab-area" className={`inline-flex items-center gap-1 text-sm font-semibold ${overHero ? "text-white" : "text-[#0A2540]"}`}><Shield size={15} />Administration</Link>}
              {user.role === "staff" && <Link to="/admin/registrants" data-testid="nav-staff-area" className={`inline-flex items-center gap-1 text-sm font-semibold ${overHero ? "text-white" : "text-[#0A2540]"}`}><Shield size={15} />Staff</Link>}
              {user.role !== "admin" && <CartLink overHero={overHero} />}
              <Link to={user.role === "admin" ? "/admin" : "/account"} data-testid="nav-my-area" className="btn-buoy whitespace-nowrap !py-2.5">
                {user.role === "admin" ? <Shield size={16} /> : <User size={16} />}{user.role === "admin" ? t("admin") : t("my_area")}
              </Link>
              <button onClick={signOut} data-testid="nav-logout" className={overHero ? "text-white" : "text-slate-500"} title={t("logout")}><LogOut size={18} /></button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className={`text-sm font-semibold ${overHero ? "text-white" : "text-[#0A2540]"}`}>{t("login")}</Link>
              <Link to="/register" data-testid="nav-register" className="btn-buoy !py-2.5">{t("register")}</Link>
            </>
          )}
        </div>
        <div className="flex items-center gap-4 lg:hidden">
          <InstallAppButton overHero={overHero} compact testid="mobile-install-app" />
          {user && user.role !== "admin" && <CartLink overHero={overHero} />}
          {user && <button onClick={signOut} data-testid="mobile-header-logout" className={overHero ? "text-white" : "text-[#0A2540]"} title={t("logout")} aria-label={t("logout")}><LogOut size={20} /></button>}
          <button className={overHero ? "text-white" : "text-[#0A2540]"} onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-200 bg-white px-6 py-6 lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-4">
            {links.map(([to, label]) => <Link key={to} to={to} className="font-semibold text-[#0A2540]">{label}</Link>)}
            <div className="flex items-center gap-3"><LangSwitch /><ThemeToggle /></div>
            {user?.role === "collaborator" && <Link to="/admin/proforma" className="font-semibold text-[#0A2540]" data-testid="mobile-collab-area">Administration</Link>}
            {user?.role === "staff" && <Link to="/admin/registrants" className="font-semibold text-[#0A2540]" data-testid="mobile-staff-area">Staff</Link>}
            {user ? <div className="flex flex-wrap items-center gap-3"><Link to={user.role === "admin" ? "/admin" : "/account"} className="btn-buoy w-fit" data-testid="mobile-my-area">{user.role === "admin" ? t("admin") : t("my_area")}</Link>
                <button onClick={signOut} className="inline-flex items-center gap-2 rounded-full border-2 border-[#0A2540] px-4 py-2 text-sm font-semibold text-[#0A2540]" data-testid="mobile-menu-logout"><LogOut size={16} />{t("logout")}</button></div>
              : <div className="flex items-center gap-3"><Link to="/login" className="font-semibold">{t("login")}</Link><Link to="/register" className="btn-buoy">{t("register")}</Link></div>}
          </div>
        </div>
      )}
    </header>
  );
};
